import { Accordion, AccordionSummary, Box, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterBackgrounds } from '../../../../../core/config/mainTheme';
import {
  useFilterDispatch,
  useFilterState,
} from '../../../shared/FinancialDataContext/FinancialDataContext';
import {
  FinancialDataContextActionTypes,
  LibraryFilterItem,
} from '../../../shared/FinancialDataContext/FinancialDataContext.types';
import FilterLibraryGroup from './FilterLibraryGroup';

interface Props {
  filterItem: LibraryFilterItem;
  index: number;
  activeCount: number;
}

const FilterLibraryRootItem: React.FC<Props> = React.memo(({ filterItem, index, activeCount }) => {
  const dispatch = useFilterDispatch();
  const { expandedLibraryFilters } = useFilterState();
  const classes = useStyles();
  const { t } = useTranslation();
  const isExpanded = useMemo(() => expandedLibraryFilters?.indexOf(filterItem.labelKey) !== -1, [
    expandedLibraryFilters,
    filterItem,
  ]);

  const renderedRootItem = useMemo(() => {
    return (
      <Accordion
        className={classes.root}
        key={`root-${filterItem.labelKey}`}
        square={true}
        data-cy={`filter-${filterItem.labelKey}`}
        expanded={isExpanded}
        onChange={() =>
          dispatch({
            type: FinancialDataContextActionTypes.ToggleLibraryFilter,
            payload: filterItem.labelKey,
          })
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box component="div" className={classes.libraryTopLevel}>
            <span className={classes.libraryTopLevelLabel}>
              {t(`companies:filters.${filterItem.labelKey}`)}
            </span>
            {activeCount >= 1 && <span className="appliedFilterCount">{activeCount}</span>}
          </Box>
        </AccordionSummary>
        {filterItem.children ? (
          <FilterLibraryGroup items={filterItem.children} path={[index]} filterItem={filterItem} />
        ) : null}
      </Accordion>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterItem, activeCount, isExpanded]);

  return <>{renderedRootItem}</>;
});

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: 0,
    boxShadow: 'none',
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize - 2,
    margin: '5px 0',
    position: 'relative',
    width: '100%',
    zIndex: 2,

    '& .MuiAccordionSummary-root': {
      padding: '0',
    },
    '& .MuiAccordionSummary-root.Mui-expanded': {
      minHeight: 30,
    },
    '& .MuiIconButton-edgeEnd': {
      marginRight: 0,
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      alignItems: 'center',
      display: 'flex',
      margin: '10px 0',
      justifyContent: 'space-between',

      '&.MuiFormControl-root': {
        width: 'auto',
      },
    },
    '& .MuiAccordionSummary-expandIcon': {
      borderLeft: `2px solid ${theme.palette.primary.contrastText}`,
      borderRadius: 0,
      padding: '11px 5px',
    },
    '& .MuiAccordionSummary-expandIcon.Mui-expanded': {
      borderLeft: 0,
      borderRight: `2px solid ${theme.palette.primary.contrastText}`,
    },
    '&.Mui-expanded': {
      margin: '5px 0',
    },
    '& .MuiAccordion-root.Mui-expanded:before': {
      opacity: 1,
    },
    '& .MuiTouchRipple-child': {
      backgroundColor: 'transparent',
    },
    '& .appliedFilterCount': {
      borderRadius: '20px',
      color: theme.palette.primary.contrastText,
      fontSize: theme.typography.fontSize - 4,
      fontWeight: 'bold',
      display: 'flex',
      height: '20px',
      alignItems: 'center',
      padding: '3px 3px',
      justifyContent: 'center',
      width: '32px',
    },
    '&[data-cy="filter-dataParameters"], &[data-cy="filter-company"]': {
      background: FilterBackgrounds.FirstChild,
      borderLeft: `3px solid ${FilterBackgrounds.FirstChildDark}`,

      '& .appliedFilterCount': {
        backgroundColor: FilterBackgrounds.FirstChildDark,
      },

      '& .MuiPaper-root': {
        borderLeft: `3px solid ${FilterBackgrounds.FirstChildDark}`,

        '& .MuiPaper-root': {
          borderLeft: `3px solid ${FilterBackgrounds.FirstChildDark}`,
        },
      },
    },
    '&[data-cy="filter-ownershipRelations"]': {
      background: FilterBackgrounds.SecondChild,
      borderLeft: `3px solid ${FilterBackgrounds.SecondChildDark}`,

      '& .appliedFilterCount': {
        backgroundColor: FilterBackgrounds.SecondChildDark,
      },

      '& .MuiPaper-root': {
        borderLeft: `3px solid ${FilterBackgrounds.SecondChildDark}`,

        '& .MuiPaper-root': {
          borderLeft: `3px solid ${FilterBackgrounds.SecondChildDark}`,
        },
      },
    },
    '&[data-cy="filter-financialInfo"]': {
      background: FilterBackgrounds.ThirdChild,
      borderLeft: `3px solid ${FilterBackgrounds.ThirdChildDark}`,

      '& .appliedFilterCount': {
        backgroundColor: FilterBackgrounds.ThirdChildDark,
      },

      '& .MuiPaper-root': {
        borderLeft: `3px solid ${FilterBackgrounds.ThirdChildDark}`,

        '& .MuiPaper-root': {
          borderLeft: `3px solid ${FilterBackgrounds.ThirdChildDark}`,
        },
      },
    },
    '&[data-cy="filter-financialRatios"]': {
      background: FilterBackgrounds.FourthChild,
      borderLeft: `3px solid ${FilterBackgrounds.FourthChildDark}`,

      '& .appliedFilterCount': {
        backgroundColor: FilterBackgrounds.FourthChildDark,
      },

      '& .MuiPaper-root': {
        borderLeft: `3px solid ${FilterBackgrounds.FourthChildDark}`,

        '& .MuiPaper-root': {
          borderLeft: `3px solid ${FilterBackgrounds.FourthChildDark}`,
        },
      },
    },
    '&[data-cy="filter-esgCompliance"]': {
      background: FilterBackgrounds.FifthChild,
      borderLeft: `3px solid ${FilterBackgrounds.FifthChildDark}`,

      '& .appliedFilterCount': {
        backgroundColor: FilterBackgrounds.FifthChildDark,
      },

      '& .MuiPaper-root': {
        borderLeft: `3px solid ${FilterBackgrounds.FifthChildDark}`,

        '& .MuiPaper-root': {
          borderLeft: `3px solid ${FilterBackgrounds.FifthChildDark}`,
        },
      },
    },
    '& div': {
      background: 'none',
    },
    '&:before': {
      backgroundColor: theme.palette.primary.contrastText,
      height: '3px',
    },
  },
  libraryTopLevel: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0 5px',
    width: '100%',
  },
  libraryTopLevelLabel: {
    width: 'calc(100% - 34px)',
  },
}));

export default FilterLibraryRootItem;
